import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { timeAgo } from "@/utils/dateUtils";
import { AppNotification } from "@/types/common";
import useApiClient from "@/api/Client";
import ENDPOINTS from "@/api/endpoints";
import Icons from "@/assets";
import BaseCard from "@/shared/components/BaseCard";
import PlaceThumbnail from "@/shared/components/PlaceThumbnail";
import { FONT_SIZES } from "@/shared/constants/typography";

interface NotificationCardProps {
  data: AppNotification;
}

const NotificationCard: React.FC<NotificationCardProps> = ({ data }) => {
  const { api } = useApiClient();
  const navigate = useNavigate();
  const [isRead, setIsRead] = useState(data.is_read);

  const { contribution, created_at, status, is_best, notification_id } =
    data || {};

  const isCongratulations = is_best && status === "reviewed";

  const handleMarkAsRead = async () => {
    try {
      if (isRead) return;

      setIsRead(true);

      if (!notification_id) return;

      await api.patch(ENDPOINTS.NOTIFICATIONS.MARK_AS_READ(notification_id));
    } catch (error) {
      console.error("Failed to mark as read:", error);
      setIsRead(false);
    } finally {
      navigate(`/contribution/view/${contribution?.contribution_id}`, {
        state: { from: "notifications" },
      });
    }
  };

  return (
    <BaseCard
      onClick={handleMarkAsRead}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: "5% 2%",
        bgcolor: isRead ? "#FFFFFF" : "#F0DEFF",
        transition: "transform 0.2s",
        "&:active": {
          transform: "scale(0.98)",
        },
        border: "1px solid #D9D9D9",
        borderRadius: 0,
        boxShadow: "0px 10px 20px 0px #0000000A",
      }}
    >
      <Box sx={{ width: "1%" }}>
        {!isRead && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "#7747FB",
            }}
          />
        )}
      </Box>
      <Box sx={{ width: "12%" }}>
        <PlaceThumbnail
          src={
            isCongratulations
              ? Icons.cupIcon
              : contribution?.media?.[0]?.photos?.[0]?.url
          }
          alt={isCongratulations ? "Congratulations!" : "Place"}
          size={{ xs: "11.65vw", sm: "24px" }}
          borderRadius={is_best ? "0" : "12px"}
          sx={{ objectFit: "contain", border: "none" }}
        />
      </Box>

      <Box sx={{ width: "70%" }}>
        <Typography
          sx={{
            fontSize: FONT_SIZES.SUBTITLE,
            fontWeight: 700,
            color: "#1E1E1E",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          {isCongratulations ? "Congratulations!" : contribution?.place?.name}
        </Typography>
        <Typography
          sx={{
            fontSize: FONT_SIZES.CAPTION,
            color: "#626262",
          }}
        >
          {isCongratulations ? (
            <>
              Your contribution has been{" "}
              <span style={{ fontWeight: 700, color: "#74CE6A" }}>
                Reviewed
              </span>{" "}
              and <span style={{ fontWeight: 700 }}>successfully added</span> to
              the {contribution?.place?.name} details.
            </>
          ) : status === "reviewed" ? (
            <>
              Your contribution has been{" "}
              <span style={{ fontWeight: 700, color: "#74CE6A" }}>
                Reviewed
              </span>{" "}
              and <span style={{ fontWeight: 700 }}>could not be added</span> to
              the {contribution?.place?.name} details due to Data mismatch.
            </>
          ) : (
            <>
              Your contribution is{" "}
              <span style={{ fontWeight: 700, color: "#F2B12F" }}>
                Under Review
              </span>
            </>
          )}
        </Typography>
      </Box>

      <Box sx={{ width: "5%" }}>
        <Typography
          sx={{
            fontSize: FONT_SIZES.CAPTION,
            color: "#626262",
          }}
        >
          {timeAgo(created_at)}
        </Typography>
      </Box>
    </BaseCard>
  );
};

export default NotificationCard;
